import React, { useState } from 'react'
import {
  X,
  Mail,
  Phone,
  MapPin,
  Calendar,
  Clock,
  Users,
  Car,
  Tag,
  FileText,
  Copy,
  Check,
  CheckCircle2,
  Clock3,
  Archive,
  ExternalLink,
} from 'lucide-react'
import type { EnquiryRecord, EnquiryStatus } from '../../types'
import { parseEnquiry } from './parseEnquiry'
import { formatBookingDate, formatBookingTime } from '../../utils/format'

interface EnquiryDetailModalProps {
  enquiry: EnquiryRecord | null
  onClose: () => void
  onStatusChange: (id: string, status: EnquiryStatus) => void
  onDelete?: (id: string) => void
}

export const EnquiryDetailModal: React.FC<EnquiryDetailModalProps> = ({
  enquiry,
  onClose,
  onStatusChange,
  onDelete,
}) => {
  const [copied, setCopied] = useState(false)

  if (!enquiry) return null

  const details = parseEnquiry(enquiry)

  const handleCopySummary = () => {
    const summary = [
      `Enquiry ID: ${enquiry.id}`,
      `Customer: ${details.customerName}`,
      `Email: ${details.customerEmail}`,
      `Phone: ${details.customerPhone || 'N/A'}`,
      `Tour: ${details.tourTitle}`,
      `Pickup: ${details.pickupLocation}`,
      `Departure: ${details.departureDate} at ${details.departureTime}`,
      `Travelers: ${details.adults} Adults, ${details.children} Children (Total: ${details.totalTravelers})`,
      details.vehicle ? `Vehicle: ${details.vehicle}` : '',
      details.estimatedPrice ? `Estimated Price: ${details.estimatedPrice}` : '',
      details.notes ? `Special Notes: ${details.notes}` : '',
      `Status: ${enquiry.status.toUpperCase()}`,
      `Submitted: ${formatBookingDate(enquiry.submittedAt)} ${formatBookingTime(enquiry.submittedAt)}`,
    ]
      .filter(Boolean)
      .join('\n')

    navigator.clipboard.writeText(summary)
    setCopied(true)
    setTimeout(() => setCopied(false), 2000)
  }

  const emailSubject = encodeURIComponent(`Re: ${enquiry.subject || 'Tour Enquiry'}`)
  const mailtoUrl = `mailto:${details.customerEmail}?subject=${emailSubject}`

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 sm:p-6 overflow-y-auto">
      {/* Backdrop */}
      <div
        className="fixed inset-0 bg-black/60 backdrop-blur-sm transition-opacity"
        onClick={onClose}
      />

      {/* Modal Dialog */}
      <div className="relative w-full max-w-2xl rounded-2xl bg-white shadow-2xl overflow-hidden z-10 flex flex-col max-h-[90vh]">
        {/* Header */}
        <div className="flex items-center justify-between border-b border-gray-100 bg-gray-50/80 px-6 py-4">
          <div className="flex items-center gap-3">
            <span
              className={`inline-flex items-center gap-1.5 rounded-full px-3 py-1 text-xs font-semibold ${
                enquiry.status === 'open'
                  ? 'bg-emerald-100 text-emerald-800'
                  : enquiry.status === 'replied'
                  ? 'bg-blue-100 text-blue-800'
                  : 'bg-gray-100 text-gray-700'
              }`}
            >
              {enquiry.status === 'open' && <Clock3 className="size-3.5" />}
              {enquiry.status === 'replied' && <CheckCircle2 className="size-3.5" />}
              {enquiry.status === 'closed' && <Archive className="size-3.5" />}
              {enquiry.status.toUpperCase()}
            </span>
            <span className="text-xs text-gray-400">ID: {enquiry.id}</span>
          </div>

          <button
            onClick={onClose}
            className="rounded-full p-1.5 text-gray-400 hover:bg-gray-200/60 hover:text-gray-700 transition-colors"
            title="Close"
          >
            <X className="size-5" />
          </button>
        </div>

        {/* Scrollable Content */}
        <div className="overflow-y-auto p-6 space-y-6">
          {/* Main Title & Customer Header */}
          <div>
            <h3 className="text-xl font-bold text-gray-900 leading-snug">
              {details.tourTitle}
            </h3>
            <p className="text-xs text-gray-500 mt-1">
              Submitted on {formatBookingDate(enquiry.submittedAt)} at {formatBookingTime(enquiry.submittedAt)}
            </p>
          </div>

          {/* Customer Contact Card */}
          <div className="rounded-xl border border-gray-200/80 bg-gray-50/50 p-4 space-y-3">
            <p className="text-xs font-bold tracking-wider text-gray-500 uppercase">
              Customer Information
            </p>
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
              <div>
                <span className="text-xs text-gray-400 block">Name</span>
                <span className="text-sm font-semibold text-gray-900">{details.customerName}</span>
              </div>

              <div>
                <span className="text-xs text-gray-400 block">Email</span>
                <a
                  href={mailtoUrl}
                  className="text-sm font-medium text-emerald-700 hover:underline flex items-center gap-1"
                >
                  <Mail className="size-3.5 shrink-0" />
                  <span className="truncate">{details.customerEmail || 'No email provided'}</span>
                </a>
              </div>

              <div>
                <span className="text-xs text-gray-400 block">Phone</span>
                {details.customerPhone ? (
                  <a
                    href={`tel:${details.customerPhone}`}
                    className="text-sm font-medium text-gray-900 hover:text-emerald-700 flex items-center gap-1"
                  >
                    <Phone className="size-3.5 shrink-0" />
                    <span>{details.customerPhone}</span>
                  </a>
                ) : (
                  <span className="text-sm text-gray-400">Not provided</span>
                )}
              </div>
            </div>
          </div>

          {/* Tour Booking Specifications */}
          <div className="rounded-xl border border-emerald-100 bg-emerald-50/30 p-4 space-y-3">
            <p className="text-xs font-bold tracking-wider text-emerald-800 uppercase flex items-center gap-1.5">
              <Tag className="size-3.5" />
              Tour & Trip Specifications
            </p>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-y-3 gap-x-6 text-sm">
              <div className="flex items-start gap-2.5">
                <MapPin className="size-4 text-emerald-600 mt-0.5 shrink-0" />
                <div>
                  <span className="text-xs text-gray-500 block">Pickup Location</span>
                  <span className="font-medium text-gray-900">{details.pickupLocation}</span>
                </div>
              </div>

              <div className="flex items-start gap-2.5">
                <Calendar className="size-4 text-emerald-600 mt-0.5 shrink-0" />
                <div>
                  <span className="text-xs text-gray-500 block">Departure Date & Time</span>
                  <span className="font-medium text-gray-900">
                    {details.departureDate || 'Requested on demand'}
                    {details.departureTime ? ` at ${details.departureTime}` : ''}
                  </span>
                </div>
              </div>

              <div className="flex items-start gap-2.5">
                <Users className="size-4 text-emerald-600 mt-0.5 shrink-0" />
                <div>
                  <span className="text-xs text-gray-500 block">Passengers</span>
                  <span className="font-medium text-gray-900">
                    {details.adults} Adults
                    {details.children > 0 ? `, ${details.children} Children` : ''}
                    <span className="text-gray-500 font-normal"> (Total: {details.totalTravelers})</span>
                  </span>
                </div>
              </div>

              <div className="flex items-start gap-2.5">
                <Car className="size-4 text-emerald-600 mt-0.5 shrink-0" />
                <div>
                  <span className="text-xs text-gray-500 block">Vehicle Preference</span>
                  <span className="font-medium text-gray-900">{details.vehicle || 'Standard Luxury Van'}</span>
                </div>
              </div>

              {details.estimatedPrice && (
                <div className="flex items-start gap-2.5 sm:col-span-2">
                  <div className="size-4 text-emerald-600 mt-0.5 font-bold flex items-center justify-center">€</div>
                  <div>
                    <span className="text-xs text-gray-500 block">Estimated Quote Price</span>
                    <span className="font-bold text-emerald-700 text-base">{details.estimatedPrice}</span>
                  </div>
                </div>
              )}
            </div>
          </div>

          {/* Special Notes / Requests */}
          {details.notes && (
            <div className="rounded-xl border border-amber-200 bg-amber-50/40 p-4 space-y-1.5">
              <span className="text-xs font-bold uppercase tracking-wider text-amber-900">
                Customer Notes / Special Requests
              </span>
              <p className="text-sm text-gray-800 whitespace-pre-wrap">{details.notes}</p>
            </div>
          )}

          {/* Full Raw Message */}
          <div className="rounded-xl border border-gray-100 bg-gray-50/50 p-4 space-y-2">
            <span className="text-xs font-bold uppercase tracking-wider text-gray-400 flex items-center gap-1.5">
              <FileText className="size-3.5" />
              Full Enquiry Transcript
            </span>
            <pre className="text-xs font-mono text-gray-700 whitespace-pre-wrap overflow-x-auto bg-white p-3 rounded-lg border border-gray-200/60 max-h-40">
              {details.rawMessage}
            </pre>
          </div>
        </div>

        {/* Footer Actions */}
        <div className="flex flex-wrap items-center justify-between gap-3 border-t border-gray-100 bg-gray-50/80 px-6 py-4">
          <div className="flex items-center gap-2">
            <span className="text-xs text-gray-500 font-medium">Status:</span>
            <select
              value={enquiry.status}
              onChange={(e) => onStatusChange(enquiry.id, e.target.value as EnquiryStatus)}
              className="text-xs font-semibold rounded-lg border border-gray-300 bg-white px-3 py-1.5 text-gray-800 shadow-sm focus:outline-none focus:ring-1 focus:ring-emerald-500"
            >
              <option value="open">Open</option>
              <option value="replied">Replied</option>
              <option value="closed">Closed</option>
            </select>

            <button
              onClick={handleCopySummary}
              className="inline-flex items-center gap-1 text-xs text-gray-600 hover:text-gray-900 px-2.5 py-1.5 rounded-lg border border-gray-200 bg-white hover:bg-gray-50 transition-colors"
              title="Copy summary for driver or email"
            >
              {copied ? <Check className="size-3.5 text-emerald-600" /> : <Copy className="size-3.5" />}
              <span>{copied ? 'Copied!' : 'Copy Summary'}</span>
            </button>
          </div>

          <div className="flex items-center gap-2">
            {details.customerPhone && (
              <a
                href={`tel:${details.customerPhone}`}
                className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg border border-gray-300 bg-white text-xs font-semibold text-gray-700 hover:bg-gray-50 transition-colors shadow-sm"
              >
                <Phone className="size-3.5" />
                <span>Call</span>
              </a>
            )}

            <a
              href={mailtoUrl}
              className="inline-flex items-center gap-1.5 px-4 py-1.5 rounded-lg bg-emerald-700 text-xs font-semibold text-white hover:bg-emerald-800 transition-colors shadow-sm"
            >
              <Mail className="size-3.5" />
              <span>Reply by Email</span>
            </a>

            {onDelete && (
              <button
                onClick={() => {
                  if (confirm('Are you sure you want to delete this enquiry?')) {
                    onDelete(enquiry.id)
                    onClose()
                  }
                }}
                className="inline-flex items-center px-3 py-1.5 rounded-lg text-xs font-medium text-red-600 hover:bg-red-50 transition-colors"
              >
                Delete
              </button>
            )}
          </div>
        </div>
      </div>
    </div>
  )
}
